import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';
import { useNotificationStore } from '../../../store/storeNotifications';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Plus, X } from '@phosphor-icons/react';
import { UploadItemAccordion } from './UploadItemAccordion';

import { spacesQueries } from '../../../queries/spaces';
import { vectorQueries } from '../../../queries/vector';
import {
  VectorService,
  type VectorAddDimensionsRequest,
  type VectorDeleteDimensionsRequest,
} from '../../../service/VectorService';

import type {
  NamespaceDimension,
  NamespaceFileDimension,
} from '../../../types/namespace-dimension';
import { dimensionSchema } from './DimensionSettingsAddDimensionForm';

type DimensionData = z.infer<typeof dimensionSchema>;

interface UploadItemDimensionsProps {
  id: string;
  fileDimensions: Array<NamespaceDimension>;
}

const STATUS_COLORS = {
  DELETING: {
    borderColor: '#dc2626',
    backgroundColor: '#dc2626',
    textColor: 'white',
  },
  ENQUEUED: {
    borderColor: '#ff9800',
    backgroundColor: '#ff9800',
    textColor: 'white',
  },
  PROCESSING: {
    borderColor: '#1976d2',
    backgroundColor: '#1976d2',
    textColor: 'white',
  },
  ERROR: {
    borderColor: '#d32f2f',
    backgroundColor: '#d32f2f',
    textColor: 'white',
  },
  ADDING: {
    borderColor: '#3650DE',
    backgroundColor: '#3650DE',
    textColor: 'white',
  },
  DEFAULT: {
    borderColor: '#d4d4d4',
    backgroundColor: 'transparent',
    textColor: 'inherit',
  },
} as const;

function UploadItemDimensions({
  id: itemId,
  fileDimensions,
}: UploadItemDimensionsProps) {
  const queryClient = useQueryClient();

  const setNotification = useNotificationStore(state => state.setNotification);

  const {
    embeddingModel,
    name,
    namespaceId,
    spaceId,

    stagingFiles,
    onStagingFilesChange,

    namespaceDimensions,
    onNamespaceDimensionsChange,

    tab,
  } = useVectorModal();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
    },
    resolver: zodResolver(dimensionSchema.omit({ isDefault: true })),
  });

  const [expanded, setExpanded] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState('');
  const [addingDimensions, setAddingDimensions] = useState<Set<string>>(
    new Set(),
  );
  const [deletingDimensions, setDeletingDimensions] = useState<Set<string>>(
    new Set(),
  );

  const isEditMode = tab === 'edit-stored-files';

  const handleExpandedToggle = () => {
    setExpanded(prev => !prev);
  };

  const updateDimensionSet = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    id: string,
    action: 'add' | 'remove',
  ) => {
    setter(prev => {
      const newSet = new Set(prev);
      if (action === 'add') newSet.add(id);
      else newSet.delete(id);
      return newSet;
    });
  };

  const invalidateQueries = () => {
    const queryKey = spaceId
      ? spacesQueries.all().queryKey
      : vectorQueries.allNamespaces().queryKey;
    return queryClient.invalidateQueries({ queryKey });
  };

  const handleDimensionCreate = (data: Omit<DimensionData, 'isDefault'>) => {
    const dimensionId = crypto.randomUUID();

    const dimensionToAdd = {
      description: data.description,
      title: data.title,
      isDefault: false,
    };

    reset();

    if (isEditMode) {
      updateDimensionSet(setAddingDimensions, dimensionId, 'add');
      addDimensionsFn({
        body: {
          dimensions: [dimensionToAdd],
          embeddingModelName: embeddingModel,
          filesIds: [itemId],
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      return;
    }

    const newFileList = stagingFiles.map(file => {
      if (file.id === itemId) {
        return {
          ...file,
          dimensions: [
            ...file.dimensions,
            { id: dimensionId, ...dimensionToAdd },
          ],
        };
      }

      return file;
    });

    onStagingFilesChange(newFileList);

    const newDimensionList = [
      ...namespaceDimensions,
      { id: dimensionId, ...dimensionToAdd },
    ];

    onNamespaceDimensionsChange(newDimensionList);
  };

  const handleDimensionAdd = (id: string) => {
    console.log('handleDimensionAdd called with id:', id);
    console.log('isEditMode:', isEditMode);
    console.log('namespaceDimensions:', namespaceDimensions);
    
    if (isEditMode) {
      addDimensionsFn({
        body: {
          dimensions: [{ id }],
          embeddingModelName: embeddingModel,
          filesIds: [itemId],
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      setSelectedDimension('');
      return;
    }

    const dimensionToAdd = namespaceDimensions.find(
      dimension => dimension.id === id,
    );

    console.log('dimensionToAdd:', dimensionToAdd);

    if (dimensionToAdd) {
      const newFileList = stagingFiles.map(file => {
        if (file.id === itemId) {
          return {
            ...file,
            dimensions: [...file.dimensions, dimensionToAdd],
          };
        }

        return file;
      });

      onStagingFilesChange(newFileList);
      setSelectedDimension('');
    }
  };

  const handleDimensionDelete = (id: string, title: string) => {
    if (isEditMode) {
      deleteDimensionsFn({
        body: {
          dimensions: [{ id, title }],
          embeddingModelName: embeddingModel,
          filesIds: [itemId],
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      return;
    }

    const newFileList = stagingFiles.map(file => {
      if (file.id === itemId) {
        const newDimensionList = file.dimensions.filter(
          dimension => dimension.id !== id,
        );

        return {
          ...file,
          dimensions: newDimensionList,
        };
      }

      return file;
    });

    onStagingFilesChange(newFileList);
  };

  const { mutate: addDimensionsFn } = useMutation({
    mutationFn: ({ body, params }: VectorAddDimensionsRequest) =>
      VectorService.addDimensions({ body, params }),
    onMutate: variables => {
      const dimension = variables.body.dimensions[0];
      if (!dimension.id) return;

      setNotification({
        type: 'warning',
        message: `Adding dimension ${dimension.title}`,
      });
      if (isEditMode) {
        updateDimensionSet(setAddingDimensions, dimension.id, 'add');
      }
    },
    onError: (_, variables) => {
      const dimension = variables.body.dimensions[0];
      setNotification({
        type: 'error',
        message: `Failed to add dimension ${dimension.title}`,
      });
      if (isEditMode && dimension.id) {
        updateDimensionSet(setAddingDimensions, dimension.id, 'remove');
      }
    },
    onSuccess: (_, variables) => {
      const dimension = variables.body.dimensions[0];
      if (isEditMode && dimension.id) {
        updateDimensionSet(setAddingDimensions, dimension.id, 'remove');
      }
      setNotification({
        type: 'success',
        message: `Successfully added dimension ${dimension.title}`,
      });
      invalidateQueries();
    },
  });

  const { mutate: deleteDimensionsFn } = useMutation({
    mutationFn: ({ body, params }: VectorDeleteDimensionsRequest) =>
      VectorService.deleteDimensions({ body, params }),
    onMutate: variables => {
      const dimension = variables.body.dimensions[0];
      setNotification({
        type: 'warning',
        message: `Deleting dimension ${dimension.title}`,
      });
      if (isEditMode && dimension.id) {
        updateDimensionSet(setDeletingDimensions, dimension.id, 'add');
      }
    },
    onError: (_, variables) => {
      const dimension = variables.body.dimensions[0];
      setNotification({
        type: 'error',
        message: `Failed to delete dimension ${dimension.title}`,
      });
      if (isEditMode && dimension.id) {
        updateDimensionSet(setDeletingDimensions, dimension.id, 'remove');
      }
    },
    onSuccess: (_, variables) => {
      const dimension = variables.body.dimensions[0];
      if (isEditMode && dimension.id) {
        updateDimensionSet(setDeletingDimensions, dimension.id, 'remove');
      }
      setNotification({
        type: 'success',
        message: `Successfully deleted dimension ${dimension.title}`,
      });
      invalidateQueries();
    },
  });

  return (
    <UploadItemAccordion
      expanded={expanded}
      title="Dimensions"
      // disabled={!searchUsingDimensions}
      onExpandedToggle={handleExpandedToggle}
    >
      <Box sx={{ '&>*+*': { marginBlockStart: '0.5rem !important' } }}>
        <Typography variant="h5" fontSize="1rem" fontWeight={700}>
          Choose an already created dimension
        </Typography>

        <List
          sx={{
            padding: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          {(() => {
            const allDimensions = new Map<string, NamespaceFileDimension>();
            fileDimensions.forEach(dim => {
              allDimensions.set(dim.id, dim as NamespaceFileDimension);
            });
            addingDimensions.forEach(dimId => {
              if (!allDimensions.has(dimId)) {
                const dim = namespaceDimensions.find(d => d.id === dimId);
                if (dim)
                  allDimensions.set(dimId, dim as NamespaceFileDimension);
              }
            });
            const dimensionsToShow = Array.from(allDimensions.values());

            if (dimensionsToShow.length === 0) {
              return (
                <Box
                  sx={{
                    width: '100%',
                    backgroundColor: '#fafafa',
                    paddingInline: '0.5rem',
                    paddingBlock: '0.375rem',
                    borderRadius: '0.25rem',
                    borderColor: '#d4d4d8',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    textAlign: 'center',
                  }}
                >
                  <Typography color="#27272a">No dimension selected</Typography>
                </Box>
              );
            }

            return dimensionsToShow.map(({ id, title, status, isDefault }) => {
              const isAdding = addingDimensions.has(id);
              const isDeleting = deletingDimensions.has(id);
              const normalizedStatus = status?.toUpperCase();
              const hasProcessingStatus =
                normalizedStatus &&
                ['ENQUEUED', 'PROCESSING', 'ERROR'].includes(normalizedStatus);

              const getStatusColors = () => {
                if (isDeleting) return STATUS_COLORS.DELETING;
                if (normalizedStatus === 'ENQUEUED')
                  return STATUS_COLORS.ENQUEUED;
                if (normalizedStatus === 'PROCESSING')
                  return STATUS_COLORS.PROCESSING;
                if (normalizedStatus === 'ERROR') return STATUS_COLORS.ERROR;
                if (isAdding) return STATUS_COLORS.ADDING;
                return STATUS_COLORS.DEFAULT;
              };

              const getDisplayText = () => {
                if (hasProcessingStatus && status) {
                  return `${title} is ${status.toLowerCase()}`;
                }
                if (isDeleting) return `Deleting ${title}`;
                if (isAdding) return `Adding ${title}`;
                return title;
              };

              const colors = getStatusColors();
              const displayText = getDisplayText();
              const isProcessing =
                isAdding || isDeleting || hasProcessingStatus;
              const canDelete = !isDefault && !isProcessing;

              return (
                <ListItem
                  key={id}
                  sx={{
                    width: 'auto',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',

                    paddingInline: '0.5rem',
                    paddingBlock: '0.25rem',

                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: colors.borderColor,
                    backgroundColor: colors.backgroundColor,
                    borderRadius: '9999vmax',
                    fontSize: '0.825rem',
                    color: colors.textColor,
                    '&:hover > button': {
                      display: canDelete ? 'inline-block' : 'none',
                    },
                  }}
                >
                  {displayText}
                  {isProcessing && (
                    <CircularProgress
                      size={12}
                      sx={{ marginLeft: '0.25rem', color: colors.textColor }}
                    />
                  )}
                  {canDelete && (
                    <Button
                      variant="text"
                      onClick={() => handleDimensionDelete(id, title)}
                      sx={{
                        width: 'auto',
                        minWidth: '0.75rem',
                        paddingInline: '0.125rem',
                        paddingBlock: '0',
                        transition: 'all 200ms ease-in',

                        display: 'none',
                      }}
                    >
                      <X size={10} />
                    </Button>
                  )}
                </ListItem>
              );
            });
          })()}
        </List>

        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel id={`dimension-${itemId}`} sx={{ color: '#9e9e9e' }}>
            Pick an already existing dimension
          </InputLabel>

          <Select
            id={`dimension-select-${itemId}`}
            fullWidth
            sx={{
              marginBottom: 0,
            }}
            label="Pick an already existing dimension"
            labelId={`dimension-${itemId}`}
            value={selectedDimension}
            onChange={evt => {
              const value = evt.target.value as string;
              handleDimensionAdd(value);
            }}
          >
            {namespaceDimensions?.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="#9e9e9e">
                  There are no existing dimensions
                </Typography>
              </MenuItem>
            ) : (
              namespaceDimensions?.map(({ id, title }) => {
                const isDisabled = Boolean(
                  fileDimensions.find(dimension => dimension.id === id),
                );

                return (
                  <MenuItem key={id} value={id} disabled={isDisabled}>
                    {title}
                  </MenuItem>
                );
              })
            )}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ position: 'relative', isolation: 'isolate' }}>
        <Typography
          sx={{
            marginInline: 'auto',
            width: 'min-content',
            paddingInline: '0.5rem',
            backgroundColor: '#fff',
          }}
          color="#71717a"
        >
          or
        </Typography>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            zIndex: -1,
            transform: 'translateY(-50%)',
            top: '50%',
            left: 0,
            right: 0,
            backgroundColor: '#e4e4e7',
            height: 1,
          }}
        />
      </Box>

      <Box sx={{ '&>*+*': { marginBlockStart: '0.5rem !important' } }}>
        <Typography variant="h5" fontSize="1rem" fontWeight={700}>
          Add a new dimension
        </Typography>

        <Box
          component="form"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
          onSubmit={handleSubmit(handleDimensionCreate)}
        >
          <TextField
            placeholder="Type dimension name"
            sx={{ flex: 1 }}
            error={!!errors.title}
            helperText={errors.title?.message}
            {...register('title')}
          />

          <TextField
            placeholder="Type dimension description"
            sx={{ flex: 1 }}
            error={!!errors.description}
            helperText={errors.description?.message}
            {...register('description')}
          />

          <Button type="submit" variant="outlined">
            Add
            <Plus size={16} />
          </Button>
        </Box>
      </Box>
    </UploadItemAccordion>
  );
}

export { UploadItemDimensions };
